<template>
    <v-container class="toggle animated" fluid>
        <v-row dense>
            <v-col cols="12">
                <v-tabs v-model="selectedTab" align-tabs="start" color="darkGreen" @update:modelValue="initUsers()">
                    <v-tab :class="{'font-weight-black': selectedTab == institute.id}" v-for="institute in instituteData" :key="institute.id" :value="institute.id">
                        {{ institute.abbrev }}
                    </v-tab>
                </v-tabs>
            </v-col>
            <v-col cols="12" md="3">
                <v-text-field
                    v-model="search"
                    variant="outlined"
                    prepend-inner-icon="mdi-magnify"
                    label="Search"
                    density="comfortable"
                    solo
                    hide-details
                    rounded="lg"
                    color="primary"
                ></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
                <v-select
                    v-model="statusFilter"
                    :items="statusOptions"
                    label="Filter by Status"
                    variant="outlined"
                    dense
                    hide-details
                    density="comfortable"
                    solo
                    rounded="lg"
                    color="primary"
                ></v-select>
            </v-col>
            <v-spacer></v-spacer>
            <v-col v-if="data.length != 0" class="d-flex flex-column" cols="12" md="2" xl="2">
                <v-btn
                @click="printWorkloadSummary(selectedTab, $store.state.semester_schoolyear.id)"
                prepend-icon="mdi-printer"
                class="mt-1 rounded-lg"
                color="green"
                height="40px"
                elevation="2">
                    Print Summary
                </v-btn>
            </v-col>
            <v-col class="d-flex flex-column" cols="12" md="3" xl="2">
                <v-btn
                @click="showAddDialog()"
                prepend-icon="mdi-plus"
                class="mt-1 rounded-lg"
                color="routesColor"
                height="40px"
                elevation="2">
                    Add Faculty Workload
                </v-btn>
            </v-col>
            <v-col cols="12">
                <v-row dense>
                    <v-col cols="12" md="3">
                        <v-card elevation="4">
                            <v-card-title>
                                For Preparation
                            </v-card-title>
                            <v-card-text class="text-h4">
                                {{ statusCounts['For Preparation'] || 0 }}
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" md="3">
                        <v-card elevation="4">
                            <v-card-title>
                                For Recommending Approval
                            </v-card-title>
                            <v-card-text class="text-h4">
                                {{ statusCounts['For Recommending Approval'] || 0 }}
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" md="3">
                        <v-card elevation="4">
                            <v-card-title>
                                For Pre-approval
                            </v-card-title>
                            <v-card-text class="text-h4">
                                {{ statusCounts['For Pre-approval'] || 0 }}
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" md="3">
                        <v-card elevation="4">
                            <v-card-title>
                                For Approval
                            </v-card-title>
                            <v-card-text class="text-h4">
                                {{ statusCounts['For Approval'] || 0 }}
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-col>
            <v-col cols="12">
                <v-row dense>
                    <v-col cols="12">
                        <v-card class="dt-container mt-2">    
                            <v-data-table
                                :mobile="isMobileOrTablet()"
                                :headers="headers"
                                :items="filteredData"
                                :search="search"
                                :loading="loading"
                                :items-per-page="15"
                                :items-per-page-options="[15,30, 50, 100, -1]"
                            >
                            <template v-slot:loading>
                                <v-progress-linear indeterminate color="primary" height="5" class="mt-1 rounded-lg"></v-progress-linear>
                            </template>
                            <template v-slot:item.datecreated="{ item }">
                                <span>{{ formatDateTime(item.datecreated)}} </span>
                            </template>
                            <template v-slot:item.status="{ item }">
                                <v-chip :color="getWorkloadStatusColor(item)" class="text-caption">
                                    <span>{{ getWorkloadStatus(item) }}</span>
                                </v-chip>
                            </template>
                            <template v-slot:[`item.action`]="{ item }">
                                <div class="d-flex justify-end ga-2">
                                    <v-btn v-show="item.isActive == false" @click="showConfirmDialog(item)" size="small" variant="outlined" text="Activate" color="green-accent-4" elevation="3"></v-btn>
                                    <v-btn @click="showEditDialog(item)" size="small" variant="outlined" text="View" color="green" elevation="3"></v-btn>
                                </div>
                            </template>
                            </v-data-table>
                        </v-card>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
    </v-container>

    <FacultyWorkloadDialog ref="FacultyWorkloadDialog" @refresh="initUsers()"/>
</template>

<script>
import axios from 'axios';
import { defineComponent } from 'vue';
import FacultyWorkloadDialog from '../Dialogs/FacultyWorkloadDialog.vue';
import { generateFacultyWorkloadSummaryPDF } from '@/composables/pdf/faculty-workload-summary';

export default defineComponent({
    components: {
       FacultyWorkloadDialog
    },
    data(){
        return {
            data: [],
            search: null,
            loading: false,
            statusFilter: 'All',
            headers: [
                { title: 'Faculty Name', align: 'start', sortable: true, key: 'faculty_name' },
                { title: 'Program/Department', align: 'start', sortable: true, key: 'department' },
                { title: 'Dated Created', align: 'start', sortable: true, key: 'datecreated' },
                { title: 'Status', align: 'start', sortable: true, key: 'status' },
                { title: 'Actions', align: 'end', sortable: false, key: 'action' },
            ],

            instituteData: [],
            selectedTab: 0,
        }
    },
    computed: {
        filteredData() {
            if (!this.statusFilter || this.statusFilter === 'All') return this.data;

            return this.data.filter(item => this.getWorkloadStatus(item) === this.statusFilter);
        },

        statusOptions() {
            const statuses = this.data.map(item => this.getWorkloadStatus(item));
            const uniqueStatuses = [...new Set(statuses)];
            return ['All', ...uniqueStatuses];
        },
        statusCounts() {
            return this.data.reduce((acc, item) => {
                const status = this.getWorkloadStatus(item);
                acc[status] = (acc[status] || 0) + 1;
                return acc;
            }, {});
        }
    },
    methods: {
        async init(){
            await axios.get(process.env.VUE_APP_SERVER_URL + '/offices/getAllInstitute')
            .then(res => {
                this.instituteData = res.data
                this.selectedTab = this.instituteData[0].id
            });

            this.initUsers()
        },
        initUsers(){
            this.loading = true
            const token = localStorage.getItem('aums-token')
            axios.get(process.env.VUE_APP_SERVER_URL + '/faculty-workload/findAllLoadByInstituteAndSemesterSchoolyear/' + this.selectedTab + '/' + this.$store.state.semester_schoolyear.id,
            { headers:
                { 
                    'Authorization': `Bearer ${token}`,
                }
            }
            ).then((res)=>{
                if(res.data){
                    this.loading = false
                    this.data = res.data
                }
            }).catch((error)=>{
                if(error.response.status == 401){
                    this.loading = false

                    localStorage.removeItem("vuex");
                    localStorage.removeItem("aums-token");
                    window.location.reload();
                }
                else{
                    console.log(error)
                }
            });
        },
        getWorkloadStatus(item) {
            if (!item.prepared_status) return "For Preparation";
            if (item.prepared_status === "prepared" && !item.recommending_approval_status) return "For Recommending Approval";
            if (item.recommending_approval_status === "recommended" && !item.pre_approved_status) return "For Pre-Approval";
            if (item.pre_approved_status === "pre-approved" && !item.approved_status) return "For Approval";
            if (item.approved_status === "approved") return "Approved";
            return "Unknown Status";
        },
        getWorkloadStatusColor(item) {
            if (!item.prepared_status) return "orange";
            if (item.prepared_status === "prepared" && !item.recommending_approval_status) return "blue";
            if (item.recommending_approval_status === "recommended" && !item.pre_approved_status) return "teal";
            if (item.pre_approved_status === "pre-approved" && !item.approved_status) return "purple";
            if (item.approved_status === "approved") return "green";
            return "gray";
        },
        async printWorkloadSummary(instituteId, semesterSchoolyearId){
            let res = await this.axiosCall('/faculty-workload/summaryOfFacultyWorkload/' + instituteId + '/' + semesterSchoolyearId, 'get', null, true)
            if (res.data){
                generateFacultyWorkloadSummaryPDF(res.data, {
                    semester: this.$store.state.semester_schoolyear.semester,
                    schoolYear: this.$store.state.semester_schoolyear.schoolyear,
                    instituteName: this.instituteData.find(inst => inst.id === instituteId)?.name || ''
                });
            }
        },
        showAddDialog(){
            this.$refs.FacultyWorkloadDialog.initAdd(this.selectedTab)
        },
        showEditDialog(data){
            this.$refs.FacultyWorkloadDialog.initUpdate(data)
        },
    },
    mounted(){
        this.init()
    }
})
</script>

<style scoped>

</style>